import ConfirmationModal from '../../../components/modal/ConfirmationModal';
import FilterForm from 'components/forms/FilterForm';
import React, { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification
} from 'antd';
import {
  IClassroomAvailableEnrollment,
  IClassroomEnrollmentsCreateResponse
} from '../../../interfaces/classrooms';
import { TextField, useModal, useTable } from '@refinedev/antd';
import { buildEnrollmentResultNotification } from '../utils/enrollment-result-notification';
import { formatDate, replaceTimeZone } from 'services/date';
import { useCreate } from '@refinedev/core';

interface IFilter {
  name: string;
  email: string;
  startsAfter: dayjs.Dayjs;
  endsBefore: dayjs.Dayjs;
}

export const ClassroomStudentsAvailableForEnrollment = ({
  classroomId
}: {
  classroomId: string;
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedType, setSelectedType] = useState('');

  const resource = `classrooms/${classroomId}/available_enrollments`;

  const onChangeType = useCallback((value: string) => {
    setSelectedType(value);
  }, []);

  const {
    tableProps,
    tableQueryResult: { refetch },
    searchFormProps
  } = useTable({
    resource,

    onSearch: ({ name, email, startsAfter, endsBefore }: IFilter) => {
      return [
        {
          field: 'course_enrollment_type',
          operator: 'eq',
          value: selectedType || undefined
        },
        {
          field: 'filter[search_user_name]',
          operator: 'eq',
          value: name
        },
        {
          field: 'filter[by_user_email]',
          operator: 'eq',
          value: email
        },
        {
          field: 'filter[created_after]',
          operator: 'eq',
          value: startsAfter && replaceTimeZone(startsAfter)
        },
        {
          field: 'filter[created_before]',
          operator: 'eq',
          value: endsBefore && replaceTimeZone(endsBefore)
        }
      ];
    },

    filters: {
      initial: []
    }
  });

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const { mutate: createEnrollments } =
    useCreate<IClassroomEnrollmentsCreateResponse>();

  /**
   * The endpoint answers `201` even when every student is refused, so refine's
   * `errorNotification` never fires for a rejection: the outcome has to be read
   * from the response body.
   */
  const notifyEnrollmentResult = useCallback(
    (payload: unknown, requestedCount: number) => {
      const { type, message, description } = buildEnrollmentResultNotification(
        payload,
        requestedCount
      );

      notification[type]({ message, description });
    },
    []
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  };
  const hasSelected = selectedRowKeys.length > 0;
  const { show, modalProps, close } = useModal();

  const handleBatchEnrollment = useCallback(async () => {
    const enrollments = selectedRowKeys.map(item => {
      const type = tableProps.dataSource?.find(e => e.id === item)?.kind;

      return {
        enrollment_id: item,
        enrollment_type: type
      };
    });
    createEnrollments(
      {
        resource: `classrooms/${classroomId}/enrollments`,
        values: { enrollments },
        errorNotification: () => {
          return {
            message: 'Ocorreu um problema ao tentar matricular',
            description: 'Algo deu errado!',
            type: 'error'
          };
        },
        // Handled in `onSuccess`, which is where the real outcome is known.
        successNotification: false
      },

      {
        onSuccess: response => {
          notifyEnrollmentResult(response?.data, enrollments.length);
          setSelectedRowKeys([]);
          refetch();
        }
      }
    );
    close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowKeys]);

  const handleCreateEnrollment = useCallback(
    (enrollment_id: string, enrollment_type: string) => () => {
      const enrollments = [{ enrollment_id, enrollment_type: enrollment_type }];
      createEnrollments(
        {
          resource: `classrooms/${classroomId}/enrollments`,
          values: { enrollments },
          errorNotification: () => {
            return {
              message: 'Ocorreu um problema ao tentar matricular',
              description: 'Algo deu errado!',
              type: 'error'
            };
          },
          // Handled in `onSuccess`, which is where the real outcome is known.
          successNotification: false
        },
        {
          onSuccess: response => {
            notifyEnrollmentResult(response?.data, enrollments.length);
            refetch();
          }
        }
      );
    },
    [classroomId, createEnrollments, notifyEnrollmentResult, refetch]
  );

  return (
    <>
      <ConfirmationModal
        closable={false}
        okText="Confirmar"
        onOk={handleBatchEnrollment}
        bodyTitle="Confirmação de matrículas"
        bodyText="Ao confirmar essa ação, ela não poderá ser desfeita. Deseja continuar?"
        width={500}
        {...modalProps}
      />
      <FilterForm searchFormProps={searchFormProps}>
        <Form.Item name="name">
          <Input placeholder="Nome do aluno" />
        </Form.Item>

        <Form.Item name="email">
          <Input placeholder="E-mail do aluno" />
        </Form.Item>

        <Form.Item name="startsAfter">
          <DatePicker
            className="w-full"
            placeholder="Início em"
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item name="endsBefore">
          <DatePicker
            className="w-full"
            placeholder="Término em"
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item name="course_enrollment_type">
          <Select
            placeholder="Tipo de curso"
            onChange={onChangeType}
            allowClear
            onClear={() => onChangeType('')}
          >
            <Select.Option value="Graduate::Courses::Enrollment">
              Pós-graduação
            </Select.Option>
            <Select.Option value="Courses::Enrollment">
              Curso livre
            </Select.Option>
          </Select>
        </Form.Item>
      </FilterForm>

      <Space align="center" className="my-4 justify-between w-full">
        <Typography.Title level={5} style={{ margin: 0, color: 'GrayText' }}>
          Alunos disponíveis para matrícula
        </Typography.Title>
        <Button onClick={() => show()} disabled={!hasSelected}>
          Matricular alunos
        </Button>
      </Space>

      <Table
        rowSelection={rowSelection}
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          totalBoundaryShowSizeChanger: 9
        }}
        rowKey="id"
      >
        <Table.Column
          dataIndex={['user', 'full_name_or_name']}
          key="name"
          title="Nome"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex={['user', 'email']}
          key="email"
          title="Email"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="registration"
          key="registration"
          title="Matrícula"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex={['course', 'name']}
          key="course"
          title="Curso"
          render={value => <TextField value={value} />}
        />
        <Table.Column<IClassroomAvailableEnrollment>
          dataIndex="kind"
          key="kind"
          title="Tipo"
          render={(_, record) => {
            const isGraduate = record.kind === 'Graduate::Courses::Enrollment';
            return (
              <Tag color={isGraduate ? 'purple' : 'blue'}>
                {isGraduate ? 'Pós-graduação' : 'Curso livre'}
              </Tag>
            );
          }}
        />
        <Table.Column
          dataIndex="created_at"
          key="course"
          title="Data início"
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY')} />
          )}
        />
        <Table.Column<IClassroomAvailableEnrollment>
          dataIndex="evaluation-actions"
          key="evaluation-actions"
          title="Ações"
          render={(_, record) => {
            return (
              <Button
                size="small"
                onClick={handleCreateEnrollment(record.id, record.kind)}
              >
                Matricular aluno
              </Button>
            );
          }}
        />
      </Table>
    </>
  );
};
